import { useState, useEffect, useCallback, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../api/client";
import LoadingScreen from "../components/LoadingScreen";
import Button from "../components/Button";
import Card from "../components/Card";
import TabBar from "../components/TabBar";
import Table from "../components/Table";
import Pagination from "../components/Pagination";
import TextField from "../components/TextField";
import Tag from "../components/Tag";
import Modal from "../components/Modal";
import Dropdown from "../components/Dropdown";
import DateRangePicker from "../components/DateRangePicker";
import { getCurrentSeasonDateRange } from "../utils/seasonDateRange";
import "./styles.css";

const tipoConfig = {
    amma_autorizaciones:  { label: 'Consulta Autorización',              variant: 'warning' },
    amma_autorizacion:    { label: 'Escaneo QR Autorización',            variant: 'default' },
    amma_precinto:        { label: 'Escaneo QR Precinto',                variant: 'default' },
    amma_captura:         { label: 'Escaneo QR Captura',                 variant: 'success' },
    amma_capturas:        { label: 'Consulta Capturas Últimas 24 horas', variant: 'info' },
};

export default function Logs() {
    const navigate = useNavigate();
    const { user, permissions } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isClearingListsCache, setIsClearingListsCache] = useState(false);
    const [isClearingAllCache, setIsClearingAllCache] = useState(false);
    const [isLoadingPhpIni, setIsLoadingPhpIni] = useState(false);
    const [isExportingLogs, setIsExportingLogs] = useState(false);
    const [showPhpIniModal, setShowPhpIniModal] = useState(false);
    const [phpIniData, setPhpIniData] = useState(null);
    const [phpIniSearch, setPhpIniSearch] = useState("");

    // Estado modal exportar temporada
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportTemporadaValue, setExportTemporadaValue] = useState("2025-2026");
    const [exportMatriculas, setExportMatriculas] = useState([]);
    const [matriculaInput, setMatriculaInput] = useState("");
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState(null);

    const temporadaOptions = ["2025-2026","2026-2027"];

    // Tabs
    const [activeTab, setActiveTab] = useState("logs");
    const tabs = [
        { id: "logs", label: "Consultas de Agentes" },
        { id: "precintos", label: "Histórico de Precintos" },
    ];

    // Tabla
    const [logsData, setLogsData] = useState({ data: [], current_page: 1, last_page: 1, per_page: 20, total: 0 });
    const [precintosData, setPrecintosData] = useState({ data: [], current_page: 1, last_page: 1, per_page: 20, total: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortBy, setSortBy] = useState("timestamp");
    const [sortOrder, setSortOrder] = useState("desc");
    const [isLoading, setIsLoading] = useState(false);

    // Filtros activos
    const [filterCotos, setFilterCotos] = useState([]);
    const [filterAgentes, setFilterAgentes] = useState([]);
    const [filterEspecies, setFilterEspecies] = useState([]);
    const [filterProvincias, setFilterProvincias] = useState([]);
    const defaultDateRange = getCurrentSeasonDateRange();
    const [filterFechaDesde, setFilterFechaDesde] = useState(defaultDateRange.startDate);
    const [filterFechaHasta, setFilterFechaHasta] = useState(defaultDateRange.endDate);

    // Convierte fecha ISO (YYYY-MM-DD) a formato DD/MM/YYYY para el DateRangePicker
    const formatDateForPicker = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    // Cambio de rango de fechas desde el DateRangePicker (igual que General)
    const handleDateRangeChange = (range) => {
        const desde = range.startDate || "";
        const hasta = range.endDate || "";
        setFilterFechaDesde(desde);
        setFilterFechaHasta(hasta);
        const updated = { ...filtersRef.current, filterFechaDesde: desde, filterFechaHasta: hasta };
        if (activeTab === "logs") {
            loadLogsData(1, searchTerm, sortBy, sortOrder,
                updated.filterCotos, updated.filterAgentes, updated.filterEspecies, updated.filterProvincias,
                updated.filterFechaDesde, updated.filterFechaHasta);
        } else {
            loadPrecintosData(1, searchTerm, sortBy, sortOrder,
                updated.filterCotos, updated.filterAgentes, updated.filterEspecies, updated.filterProvincias,
                updated.filterFechaDesde, updated.filterFechaHasta);
        }
    };

    // Opciones para los dropdowns
    const [cotosOptions, setCotosOptions] = useState([]);
    const [agentesOptions, setAgentesOptions] = useState([]);
    const [agentesPrecintosOptions, setAgentesPrecintosOptions] = useState([]);
    const [especiesOptions, setEspeciesOptions] = useState([]);
    const [provinciasOptions, setProvinciasOptions] = useState([]);

    // Ref para mantener filtros actualizados en closures
    const filtersRef = useRef({ filterCotos: [], filterAgentes: [], filterEspecies: [], filterProvincias: [], filterFechaDesde: defaultDateRange.startDate, filterFechaHasta: defaultDateRange.endDate });
    useEffect(() => {
        filtersRef.current = { filterCotos, filterAgentes, filterEspecies, filterProvincias, filterFechaDesde, filterFechaHasta };
    }, [filterCotos, filterAgentes, filterEspecies, filterProvincias, filterFechaDesde, filterFechaHasta]);

    // Cargar opciones de filtros al montar
    useEffect(() => {
        const loadFilterOptions = async () => {
            // allSettled: el fallo de una petición no debe dejar el resto de filtros vacíos.
            const [cotosRes, agentesRes, agentesPrecintosRes, especiesRes, provinciasRes] = await Promise.allSettled([
                api.get('/cotos'),
                api.get('/logs/agentes'),
                api.get('/logs/precintos/agentes'),
                api.get('/especies'),
                api.get('/provincias'),
            ]);

            const dataOf = (res, label) => {
                if (res.status === 'fulfilled') return res.value.data;
                Sentry.captureException(new Error(`Error loading filter options (${label}): ` + (res.reason?.message ?? 'unknown')));
                return [];
            };

            setCotosOptions(dataOf(cotosRes, 'cotos'));
            setAgentesOptions(dataOf(agentesRes, 'logs/agentes'));
            setAgentesPrecintosOptions(dataOf(agentesPrecintosRes, 'precintos/agentes'));
            setEspeciesOptions(dataOf(especiesRes, 'especies'));

            const provincias = (dataOf(provinciasRes, 'provincias') || [])
                .filter((p) => {
                    const label = typeof p === 'string' ? p : (p.label ?? p.nombre ?? '');
                    return !label.toLowerCase().includes('demo');
                })
                .map((p) => {
                    if (typeof p === 'string') return p.toUpperCase();
                    const label = p.label ?? p.nombre ?? '';
                    return { ...p, label: label.toUpperCase() };
                });
            setProvinciasOptions(provincias);
            setLoading(false);
        };
        loadFilterOptions();
    }, []);

    const buildParams = (page, search, sortColumn, sortDir, cotos, agentes, especies, prov, fechaDesde = "", fechaHasta = "") => {
        const params = new URLSearchParams({
            page: page.toString(),
            per_page: "20",
            search,
            sort_by: sortColumn,
            sort_order: sortDir,
        });
        cotos.forEach(id => params.append('coto_ids[]', id));
        agentes.forEach(id => params.append('user_ids[]', id));
        especies.forEach(id => params.append('especie_ids[]', id));
        prov.forEach(p => params.append('provincias[]', p));
        if (fechaDesde) params.set('fecha_desde', fechaDesde);
        if (fechaHasta) params.set('fecha_hasta', fechaHasta);
        return params;
    };

    const loadLogsData = async (
        page = 1, search = "", sortColumn = sortBy, sortDir = sortOrder,
        cotos = filtersRef.current.filterCotos,
        agentes = filtersRef.current.filterAgentes,
        especies = filtersRef.current.filterEspecies,
        prov = filtersRef.current.filterProvincias,
        fechaDesde = filtersRef.current.filterFechaDesde,
        fechaHasta = filtersRef.current.filterFechaHasta,
    ) => {
        setIsLoading(true);
        try {
            const params = buildParams(page, search, sortColumn, sortDir, cotos, agentes, especies, prov, fechaDesde, fechaHasta);
            const response = await api.get("/logs/data?" + params);
            setLogsData(response.data);
            setCurrentPage(response.data.current_page);
        } catch (err) {
            Sentry.captureException(new Error("Error loading logs: " + err.message));
        } finally {
            setIsLoading(false);
        }
    };

    const loadPrecintosData = async (
        page = 1, search = "", sortColumn = sortBy, sortDir = sortOrder,
        cotos = filtersRef.current.filterCotos,
        agentes = filtersRef.current.filterAgentes,
        especies = filtersRef.current.filterEspecies,
        prov = filtersRef.current.filterProvincias,
        fechaDesde = filtersRef.current.filterFechaDesde,
        fechaHasta = filtersRef.current.filterFechaHasta,
    ) => {
        setIsLoading(true);
        try {
            const params = buildParams(page, search, sortColumn, sortDir, cotos, agentes, especies, prov, fechaDesde, fechaHasta);
            const response = await api.get("/logs/precintos?" + params);
            setPrecintosData(response.data);
            setCurrentPage(response.data.current_page);
        } catch (err) {
            Sentry.captureException(new Error("Error loading precintos: " + err.message));
        } finally {
            setIsLoading(false);
        }
    };

    // Manejador genérico de cambio de filtro
    const handleFilterChange = (setter, filterKey, newValues) => {
        setter(newValues);
        const updated = { ...filtersRef.current, [filterKey]: newValues };
        if (activeTab === "logs") {
            loadLogsData(1, searchTerm, sortBy, sortOrder,
                updated.filterCotos, updated.filterAgentes, updated.filterEspecies, updated.filterProvincias,
                updated.filterFechaDesde, updated.filterFechaHasta);
        } else {
            loadPrecintosData(1, searchTerm, sortBy, sortOrder,
                updated.filterCotos, updated.filterAgentes, updated.filterEspecies, updated.filterProvincias,
                updated.filterFechaDesde, updated.filterFechaHasta);
        }
    };

    // Limpiar todos los filtros
    const handleClearFilters = () => {
        setFilterCotos([]);
        setFilterAgentes([]);
        setFilterEspecies([]);
        setFilterProvincias([]);
        setFilterFechaDesde(defaultDateRange.startDate);
        setFilterFechaHasta(defaultDateRange.endDate);
        if (activeTab === "logs") {
            loadLogsData(1, searchTerm, sortBy, sortOrder, [], [], [], [], defaultDateRange.startDate, defaultDateRange.endDate);
        } else {
            loadPrecintosData(1, searchTerm, sortBy, sortOrder, [], [], [], [], defaultDateRange.startDate, defaultDateRange.endDate);
        }
    };

    const isDefaultDateRange = filterFechaDesde === defaultDateRange.startDate && filterFechaHasta === defaultDateRange.endDate;
    const hasActiveFilters = filterCotos.length > 0 || filterAgentes.length > 0 || filterEspecies.length > 0 || filterProvincias.length > 0 || !isDefaultDateRange;

    // Handlers de exportación de temporada
    const handleAddMatricula = () => {
        const val = matriculaInput.trim().toUpperCase();
        if (val && !exportMatriculas.includes(val)) {
            setExportMatriculas((prev) => [...prev, val]);
        }
        setMatriculaInput("");
    };

    const handleMatriculaKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddMatricula();
        }
    };

    const handleRemoveMatricula = (mat) => {
        setExportMatriculas((prev) => prev.filter((m) => m !== mat));
    };

    const handleExportTemporada = async () => {
        setExportError(null);
        if (exportMatriculas.length === 0) {
            setExportError("Añade al menos una matrícula.");
            return;
        }
        setIsExporting(true);
        try {
            const response = await api.post(
                "/logs/export-temporada",
                { temporada: exportTemporadaValue, matriculas: exportMatriculas },
                { responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `temporada_${exportTemporadaValue}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setShowExportModal(false);
        } catch (err) {
            Sentry.captureException(new Error("Error exporting temporada: " + err.message));
            setExportError(err.response?.data?.error || "Error al generar el Excel.");
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportLogs = async () => {
        setIsExportingLogs(true);
        try {
            const { filterCotos: fc, filterAgentes: fa, filterEspecies: fe, filterProvincias: fp, filterFechaDesde: fdd, filterFechaHasta: fdh } = filtersRef.current;
            const params = buildParams(1, searchTerm, sortBy, sortOrder, fc, fa, fe, fp, fdd, fdh);
            const response = await api.get("/logs/export?" + params, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `logs_agentes_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            Sentry.captureException(new Error("Error exporting logs: " + err.message));
            alert(err.response?.data?.error || "Error al generar el Excel.");
        } finally {
            setIsExportingLogs(false);
        }
    };

    const handleClearListsCache = async () => {
        setIsClearingListsCache(true);
        try {
            await api.post('/mantenimiento/optimize-clear');
        } catch (error) {
            Sentry.captureException(new Error("Error clearing cache (optimize:clear): " + error.message));
            alert(error.response?.data?.error || 'Error al limpiar la caché');
        } finally {
            setIsClearingListsCache(false);
        }
    };

    const handleClearAllCache = async () => {
        setIsClearingAllCache(true);
        try {
            await api.post('/mantenimiento/route-cache');
        } catch (error) {
            Sentry.captureException(new Error("Error caching routes (route:cache): " + error.message));
            alert(error.response?.data?.error || 'Error al cachear las rutas');
        } finally {
            setIsClearingAllCache(false);
        }
    };

    const handlePhpIni = async () => {
        setIsLoadingPhpIni(true);
        try {
            const response = await api.get('/mantenimiento/php-info');
            setPhpIniData(response.data);
            setPhpIniSearch("");
            setShowPhpIniModal(true);
        } catch (error) {
            Sentry.captureException(new Error("Error fetching php.ini: " + error.message));
            alert(error.response?.data?.error || 'Error al obtener php.ini');
        } finally {
            setIsLoadingPhpIni(false);
        }
    };

    const handleRefreshMaterialized = async () => {
        setIsRefreshing(true);
        try {
            const response = await api.post('/logs/refresh-materialized');
            if (response.data?.redirect) {
                navigate('/general');
            }
        } catch (error) {
            Sentry.captureException(new Error("Error refreshing materialized views: " + error.message));
            alert(error.response?.data?.error || 'Error al refrescar las vistas materializadas');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setCurrentPage(1);
        setSearchTerm("");
        setSortBy("timestamp");
        setSortOrder("desc");
        setFilterCotos([]);
        setFilterAgentes([]);
        setFilterEspecies([]);
        setFilterProvincias([]);
        setFilterFechaDesde(defaultDateRange.startDate);
        setFilterFechaHasta(defaultDateRange.endDate);

        if (tabId === "logs") {
            loadLogsData(1, "", "timestamp", "desc", [], [], [], [], defaultDateRange.startDate, defaultDateRange.endDate);
        } else {
            loadPrecintosData(1, "", "timestamp", "desc", [], [], [], [], defaultDateRange.startDate, defaultDateRange.endDate);
        }
    };

    const handlePageChange = useCallback((page) => {
        setCurrentPage(page);
        const { filterCotos: fc, filterAgentes: fa, filterEspecies: fe, filterProvincias: fp, filterFechaDesde: fdd, filterFechaHasta: fdh } = filtersRef.current;
        if (activeTab === "logs") {
            loadLogsData(page, searchTerm, sortBy, sortOrder, fc, fa, fe, fp, fdd, fdh);
        } else {
            loadPrecintosData(page, searchTerm, sortBy, sortOrder, fc, fa, fe, fp, fdd, fdh);
        }
    }, [activeTab, searchTerm, sortBy, sortOrder]);

    const handleSort = useCallback((columnKey) => {
        const newSortOrder = sortBy === columnKey && sortOrder === "asc" ? "desc" : "asc";
        setSortBy(columnKey);
        setSortOrder(newSortOrder);
        const { filterCotos: fc, filterAgentes: fa, filterEspecies: fe, filterProvincias: fp, filterFechaDesde: fdd, filterFechaHasta: fdh } = filtersRef.current;
        if (activeTab === "logs") {
            loadLogsData(1, searchTerm, columnKey, newSortOrder, fc, fa, fe, fp, fdd, fdh);
        } else {
            loadPrecintosData(1, searchTerm, columnKey, newSortOrder, fc, fa, fe, fp, fdd, fdh);
        }
    }, [sortBy, sortOrder, searchTerm, activeTab]);

    // Búsqueda con debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const { filterCotos: fc, filterAgentes: fa, filterEspecies: fe, filterProvincias: fp, filterFechaDesde: fdd, filterFechaHasta: fdh } = filtersRef.current;
            if (activeTab === "logs") {
                loadLogsData(1, searchTerm, sortBy, sortOrder, fc, fa, fe, fp, fdd, fdh);
            } else {
                loadPrecintosData(1, searchTerm, sortBy, sortOrder, fc, fa, fe, fp, fdd, fdh);
            }
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // Columnas para tabla de logs (Consultas de Agentes)
    const logsColumns = [
        {
            key: "tipo",
            label: "Tipo de Consulta",
            sortable: false,
            minWidth: "210px",
            render: (value) => {
                const cfg = tipoConfig[value] || { label: value, variant: 'default' };
                return <Tag variant={cfg.variant}>{cfg.label}</Tag>;
            },
        },
        {
            key: "user",
            label: "Agente",
            sortable: true,
            minWidth: "130px",
            render: (value) => (
                <span style={{ color: "var(--dk-green-700)", fontWeight: 600 }}>{value}</span>
            ),
        },
        {
            key: "description",
            label: "Consulta",
            sortable: true,
            minWidth: "220px",
        },
        {
            key: "coto",
            label: "Coto",
            sortable: false,
            minWidth: "110px",
            render: (value) => value ? <Tag variant="success">{value}</Tag> : <Tag variant="default">N/A</Tag>,
        },
        {
            key: "provincia",
            label: "Provincia",
            sortable: false,
            minWidth: "110px",
            render: (value) => value ?? <span style={{ color: "#9ca3af" }}>—</span>,
        },
        {
            key: "especie",
            label: "Especie",
            sortable: false,
            minWidth: "120px",
            render: (value) => value ?? <span style={{ color: "#9ca3af" }}>—</span>,
        },
        {
            key: "timestamp",
            label: "Fecha",
            sortable: true,
            minWidth: "170px",
        },
    ];

    // Columnas para tabla de precintos
    const precintosColumns = [
        {
            key: "nr",
            label: "Precinto",
            sortable: true,
            minWidth: "130px",
            render: (value) => (
                <span style={{ color: "var(--dk-green-700)", fontWeight: 600 }}>{value}</span>
            ),
        },
        {
            key: "estado",
            label: "Estado",
            sortable: false,
            minWidth: "120px",
            render: (value) => {
                const colorMap = {
                    activado: "#00a070",
                    desactivado: "#ef4444",
                    cazado: "#eb8f25",
                };
                const color = colorMap[value] || "#9ca3af";
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{
                            width: "8px", height: "8px", borderRadius: "50%",
                            backgroundColor: color,
                        }} />
                        <span style={{ fontWeight: 600, color }}>{value}</span>
                    </div>
                );
            },
        },
        {
            key: "coto",
            label: "Coto",
            sortable: true,
            minWidth: "110px",
            render: (value) => value ? <Tag variant="success">{value}</Tag> : <Tag variant="default">N/A</Tag>,
        },
        {
            key: "provincia",
            label: "Provincia",
            sortable: false,
            minWidth: "110px",
            render: (value) => value ?? <span style={{ color: "#9ca3af" }}>—</span>,
        },
        {
            key: "especie",
            label: "Especie",
            sortable: true,
            minWidth: "140px",
        },
        {
            key: "sexo",
            label: "Sexo",
            sortable: true,
            minWidth: "70px",
            render: (value) => (
                <span>
                    {value === "m" || value === "M" || value === "Macho" ? "Macho" : value === "f" || value === "F" || value === "Hembra" ? "Hembra" : "-"}
                </span>
            ),
        },
        {
            key: "usuario",
            label: "Cazador",
            sortable: true,
            minWidth: "130px",
            render: (value, row) => (
                row?.dni
                    ? <span style={{ color: "var(--dk-green-700)", fontWeight: 600 }}>{String(row.dni).toUpperCase()}</span>
                    : <span style={{ color: "#9ca3af" }}>—</span>
            ),
        },
        {
            key: "timestamp",
            label: "Fecha",
            sortable: true,
            minWidth: "180px",
        },
    ];

    const currentData = activeTab === "logs" ? logsData : precintosData;
    const currentColumns = activeTab === "logs" ? logsColumns : precintosColumns;

    // Bloque de filtros reutilizable
    const filtersBlock = (
        <>
            <Dropdown
                options={provinciasOptions}
                value={filterProvincias}
                onChange={(v) => handleFilterChange(setFilterProvincias, 'filterProvincias', v)}
                placeholder="Provincia"
                multiple
                searchable
            />
            <Dropdown
                options={cotosOptions}
                value={filterCotos}
                onChange={(v) => handleFilterChange(setFilterCotos, 'filterCotos', v)}
                placeholder="Coto"
                multiple
                searchable
            />
            <Dropdown
                options={activeTab === "precintos" ? agentesPrecintosOptions : agentesOptions}
                value={filterAgentes}
                onChange={(v) => handleFilterChange(setFilterAgentes, 'filterAgentes', v)}
                placeholder={activeTab === "precintos" ? "Cazador" : "Agente"}
                multiple
                searchable
            />
            <Dropdown
                options={especiesOptions}
                value={filterEspecies}
                onChange={(v) => handleFilterChange(setFilterEspecies, 'filterEspecies', v)}
                placeholder="Especie"
                multiple
                searchable
            />
            <DateRangePicker
                startDate={formatDateForPicker(filterFechaDesde)}
                endDate={formatDateForPicker(filterFechaHasta)}
                onChange={handleDateRangeChange}
            />
            {hasActiveFilters && (
                <button
                    onClick={handleClearFilters}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#6b7280",
                        fontSize: "13px",
                        padding: "4px 8px",
                        textDecoration: "underline",
                    }}
                >
                    Limpiar filtros
                </button>
            )}
        </>
    );

    return (
        <div className="page-general">
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "24px"
            }}>
                <h1 className="page-title" style={{ margin: 0 }}>Logs</h1>

                {user?.rol === 'admin' && (
                    <div style={{ display: "flex", gap: "12px" }}>
                        <Button
                            variant="primary"
                            onClick={() => {
                                setExportMatriculas([]);
                                setMatriculaInput("");
                                setExportError(null);
                                setExportTemporadaValue("2025-2026");
                                setShowExportModal(true);
                            }}
                        >
                            Exportar Temporada
                        </Button>
                        <Button
                            variant="accent"
                            onClick={handleRefreshMaterialized}
                            disabled={isRefreshing}
                        >
                            {isRefreshing ? 'Refrescando...' : 'Refrescar Vistas Materializadas'}
                        </Button>
                    </div>
                )}
            </div>

            <div className="page-tabs">
                <TabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
            </div>

            {activeTab === "logs" && (
                <div className="page-content">
                    <p className="page-description-text">
                        Busca las consultas realizadas por los agentes de campo en el sistema.
                    </p>

                    <Card>
                        <div className="card-content">
                            <div className="table-filters" style={{ marginBottom: "24px", flexWrap: "wrap" }}>
                                <TextField
                                    placeholder="Buscar por agente, coto, consulta..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    withIcon={true}
                                    className="table-search"
                                />
                                {filtersBlock}
                                <Button
                                    variant="secondary"
                                    size="s"
                                    onClick={handleExportLogs}
                                    disabled={isExportingLogs}
                                    style={{ marginLeft: "auto" }}
                                >
                                    {isExportingLogs ? "Generando..." : "Descargar Excel"}
                                </Button>
                            </div>

                            {isLoading ? (
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px", color: "var(--dk-green-600)" }}>
                                    Cargando datos...
                                </div>
                            ) : logsData.data && logsData.data.length > 0 ? (
                                <>
                                    <Table
                                        columns={logsColumns}
                                        data={logsData.data}
                                        onSort={handleSort}
                                        sortedColumn={sortBy}
                                        sortOrder={sortOrder}
                                    />
                                    {logsData.last_page > 1 && (
                                        <div className="table-pagination" style={{ marginTop: "24px" }}>
                                            <Pagination
                                                currentPage={logsData.current_page || 1}
                                                totalPages={logsData.last_page || 1}
                                                onPageChange={handlePageChange}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div style={{ textAlign: "center", padding: "48px 24px", color: "#6b7280" }}>
                                    <p style={{ fontSize: "18px", marginBottom: "8px" }}>No hay registros disponibles</p>
                                    <p style={{ fontSize: "14px" }}>Las consultas de agentes aparecerán aquí</p>
                                </div>
                            )}
                        </div>
                    </Card>
                </div>
            )}

            {activeTab === "precintos" && (
                <div className="page-content">
                    <div className="page-table-section">
                        <div className="table-section-header">
                            <h3 className="table-section-title">Precintos</h3>
                        </div>

                        <div className="table-filters" style={{ marginBottom: "24px", flexWrap: "wrap" }}>
                            <TextField
                                placeholder="Buscar por precinto, coto, especie, DNI..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                withIcon={true}
                                className="table-search"
                            />
                            {filtersBlock}
                        </div>

                        {isLoading ? (
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px", color: "var(--dk-green-600)" }}>
                                Cargando datos...
                            </div>
                        ) : precintosData.data && precintosData.data.length > 0 ? (
                            <>
                                <Table
                                    columns={precintosColumns}
                                    data={precintosData.data}
                                    onSort={handleSort}
                                    sortedColumn={sortBy}
                                    sortOrder={sortOrder}
                                />
                                {precintosData.last_page > 1 && (
                                    <div className="table-pagination">
                                        <Pagination
                                            currentPage={precintosData.current_page || 1}
                                            totalPages={precintosData.last_page || 1}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div style={{ textAlign: "center", padding: "48px 24px", color: "#6b7280" }}>
                                <p style={{ fontSize: "18px", marginBottom: "8px" }}>No hay registros disponibles</p>
                                <p style={{ fontSize: "14px" }}>Los históricos de precintos aparecerán aquí</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Modal exportar temporada */}
            <Modal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                title="Exportar Temporada"
                onApply={handleExportTemporada}
                applyLabel={isExporting ? "Generando..." : "Descargar Excel"}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                    <div>
                        <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "14px" }}>
                            Temporada
                        </label>
                        <select
                            value={exportTemporadaValue}
                            onChange={(e) => setExportTemporadaValue(e.target.value)}
                            style={{
                                width: "100%", padding: "8px 12px",
                                border: "1px solid var(--dk-green-200, #d1d5db)",
                                borderRadius: "6px", fontSize: "14px",
                                backgroundColor: "#fff", cursor: "pointer",
                            }}
                        >
                            {temporadaOptions.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label style={{ display: "block", fontWeight: 600, marginBottom: "8px", fontSize: "14px" }}>
                            Matrículas <span style={{ color: "#6b7280", fontWeight: 400 }}>(mínimo 1)</span>
                        </label>
                        <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
                            <input
                                type="text"
                                value={matriculaInput}
                                onChange={(e) => setMatriculaInput(e.target.value)}
                                onKeyDown={handleMatriculaKeyDown}
                                placeholder="Ej: P-10954"
                                style={{
                                    flex: 1, padding: "8px 12px",
                                    border: "1px solid var(--dk-green-200, #d1d5db)",
                                    borderRadius: "6px", fontSize: "14px",
                                }}
                            />
                            <Button variant="primary" size="s" onClick={handleAddMatricula}>
                                Añadir
                            </Button>
                        </div>

                        {exportMatriculas.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                {exportMatriculas.map((mat) => (
                                    <span
                                        key={mat}
                                        style={{
                                            display: "inline-flex", alignItems: "center", gap: "6px",
                                            padding: "4px 10px",
                                            backgroundColor: "var(--dk-green-100, #dcfce7)",
                                            color: "var(--dk-green-700, #15803d)",
                                            borderRadius: "999px", fontSize: "13px", fontWeight: 600,
                                        }}
                                    >
                                        {mat}
                                        <button
                                            onClick={() => handleRemoveMatricula(mat)}
                                            style={{
                                                background: "none", border: "none", cursor: "pointer",
                                                padding: 0, lineHeight: 1,
                                                color: "var(--dk-green-600, #16a34a)", fontWeight: 700, fontSize: "14px",
                                            }}
                                            aria-label={`Eliminar ${mat}`}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {exportMatriculas.length === 0 && (
                            <p style={{ fontSize: "13px", color: "#9ca3af", margin: 0 }}>
                                Escribe una matrícula y pulsa Añadir o Enter.
                            </p>
                        )}
                    </div>

                    {exportError && (
                        <p style={{ color: "#dc2626", fontSize: "14px", margin: 0 }}>{exportError}</p>
                    )}
                </div>
            </Modal>

            {/* Modal PHP Info */}
            <Modal
                isOpen={showPhpIniModal}
                onClose={() => setShowPhpIniModal(false)}
                title="PHP Info"
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    <input
                        type="text"
                        placeholder="Buscar directiva..."
                        value={phpIniSearch}
                        onChange={(e) => setPhpIniSearch(e.target.value)}
                        style={{
                            padding: "8px 12px",
                            border: "1px solid var(--dk-green-200, #d1d5db)",
                            borderRadius: "6px", fontSize: "14px",
                        }}
                    />
                    <div style={{ maxHeight: "480px", overflowY: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead>
                                <tr style={{ position: "sticky", top: 0, background: "#f9fafb" }}>
                                    <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: 600 }}>Directiva</th>
                                    <th style={{ textAlign: "left", padding: "8px 10px", borderBottom: "1px solid #e5e7eb", fontWeight: 600 }}>Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {phpIniData && Object.entries(phpIniData)
                                    .filter(([key]) => key.toLowerCase().includes(phpIniSearch.toLowerCase()))
                                    .map(([key, value]) => (
                                        <tr key={key} style={{ borderBottom: "1px solid #f3f4f6" }}>
                                            <td style={{ padding: "6px 10px", color: "var(--dk-green-700)", fontFamily: "monospace" }}>{key}</td>
                                            <td style={{ padding: "6px 10px", fontFamily: "monospace", wordBreak: "break-all" }}>
                                                {value === false || value === "" ? <span style={{ color: "#9ca3af" }}>—</span> : String(value)}
                                            </td>
                                        </tr>
                                    ))
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
